import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import BulkActionBar from "@/components/BulkActionBar";
import useBulkSelection from "@/hooks/useBulkSelection";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "secondary", confirmed: "outline", packed: "outline", ready: "outline",
  assigned: "outline", out_for_delivery: "outline", delivered: "default",
  cancelled: "destructive", returned: "destructive", refunded: "destructive",
};

const FILTER_TABS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "packed", label: "Packed" },
  { value: "ready", label: "Ready" },
  { value: "out_for_delivery", label: "Out" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const BULK_STATUSES = ["confirmed", "packed", "ready", "out_for_delivery", "delivered", "cancelled"];

export default function Orders() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Sprint — Dashboard donut / drill-down passes `?status=pending` and we
  // seed the tab from it. Kept in sync so browser Back keeps the tab too.
  const initialStatus = searchParams.get("status") || "";
  const [tab, setTab] = useState(initialStatus);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    payment_method: "",   // "" | "cod" | "razorpay" | "wallet"
    payment_status: "",   // "" | "paid" | "pending" | "failed"
    date_from: "",
    date_to: "",
    amount_min: "",
    amount_max: "",
  });

  const [bulkTarget, setBulkTarget] = useState("");
  const sel = useBulkSelection();

  const filterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "" && v !== null).length,
    [filters]
  );

  const fetchRows = async (pg = page, status = tab, query = q, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: pg, limit });
      if (status) qs.set("status", status);
      const { data } = await api.get(`/orders?${qs}`);
      let items = data.items || [];
      // Client-side filters
      if (query) {
        const s = query.toLowerCase();
        items = items.filter((r) =>
          (r.order_number || "").toLowerCase().includes(s) ||
          (r.address?.name || "").toLowerCase().includes(s) ||
          (r.address?.phone || "").includes(s)
        );
      }
      if (f.payment_method) items = items.filter((r) => (r.payment?.method || "").toLowerCase() === f.payment_method);
      if (f.payment_status) items = items.filter((r) => (r.payment?.status || "").toLowerCase() === f.payment_status);
      if (f.date_from) {
        const t = new Date(f.date_from).getTime();
        items = items.filter((r) => new Date(r.created_at).getTime() >= t);
      }
      if (f.date_to) {
        const t = new Date(f.date_to).getTime() + 24 * 3600 * 1000;
        items = items.filter((r) => new Date(r.created_at).getTime() <= t);
      }
      if (f.amount_min) items = items.filter((r) => Number(r.pricing?.total || 0) >= Number(f.amount_min));
      if (f.amount_max) items = items.filter((r) => Number(r.pricing?.total || 0) <= Number(f.amount_max));
      setRows(items);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load orders"));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(page, tab, q, filters); /* eslint-disable-next-line */ }, [page, tab]);

  const runBulkStatus = async (targetStatus) => {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (!targetStatus) { toast.error("Pick a target status first"); return; }
    if (!window.confirm(`Mark ${ids.length} order${ids.length > 1 ? "s" : ""} as "${targetStatus.replace(/_/g, " ")}"?`)) return;
    const t = toast.loading(`Updating ${ids.length}…`);
    const results = await Promise.allSettled(
      ids.map((id) => api.patch(`/orders/${id}/status`, { status: targetStatus, note: "Bulk status update" }))
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = ids.length - ok;
    toast.dismiss(t);
    if (fail === 0) toast.success(`Updated ${ok} order${ok !== 1 ? "s" : ""}`);
    else toast.warning(`${ok} ok · ${fail} failed`);
    sel.clear();
    fetchRows(page, tab, q, filters);
  };

  const columns = [
    { key: "order_number", header: "Order", render: (r) => (
      <div>
        <div className="font-mono font-semibold">{r.order_number}</div>
        <div className="text-xs text-muted-foreground">{r.channel}</div>
      </div>
    )},
    { key: "address", header: "Customer", render: (r) => (
      <div>
        <div className="text-sm">{r.address?.name || "—"}</div>
        <div className="text-xs text-muted-foreground">{r.address?.phone}</div>
      </div>
    )},
    { key: "items", header: "Items", className: "w-20 text-right", render: (r) => r.items?.length ?? 0 },
    { key: "pricing", header: "Total", className: "w-28 text-right", render: (r) => <span className="font-semibold">{money(r.pricing?.total)}</span> },
    { key: "payment", header: "Payment", className: "w-44",
      render: (r) => (
        <div className="text-xs leading-tight">
          <div className="font-semibold uppercase">{r.payment?.method || "—"}</div>
          <div className="text-[10px] text-muted-foreground capitalize">{r.payment?.status || "pending"}</div>
          {r.payment?.provider_payment_id && (
            <div className="text-[10px] text-muted-foreground font-mono truncate" title={r.payment.provider_payment_id}>{r.payment.provider_payment_id}</div>
          )}
          {r.payment?.paid_at && (
            <div className="text-[10px] text-muted-foreground">{fmtDate(r.payment.paid_at)}</div>
          )}
        </div>
      ) },
    { key: "status", header: "Status", className: "w-32",
      render: (r) => <Badge variant={STATUS_TONE[r.status] || "secondary"} className="capitalize">{r.status?.replace(/_/g, " ")}</Badge> },
    { key: "created_at", header: "Placed", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div className="p-4 md:p-8" data-testid="orders-page">
      <PageHeader
        title="Orders"
        subtitle="All channels — web, mobile, POS"
        actions={
          <Button
            variant={filtersOpen || filterCount > 0 ? "default" : "outline"}
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="orders-filter-toggle"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters{filterCount > 0 ? ` · ${filterCount}` : ""}
          </Button>
        }
      />

      <Tabs value={tab || "all"} onValueChange={(v) => {
        const next = v === "all" ? "" : v;
        setTab(next);
        setPage(1);
        // Keep URL in sync so refresh + Back preserve the filter
        const params = new URLSearchParams(searchParams);
        if (next) params.set("status", next); else params.delete("status");
        setSearchParams(params, { replace: true });
      }} className="mb-4">
        <TabsList className="flex-wrap h-auto">
          {FILTER_TABS.map((t) => (
            <TabsTrigger key={t.value || "all"} value={t.value || "all"} data-testid={`orders-tab-${t.value || "all"}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filtersOpen && (
        <div className="mb-4 rounded-2xl border bg-card p-4 space-y-3" data-testid="orders-filter-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-[11px]">Payment method</Label>
              <Select value={filters.payment_method || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-pay-method"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="cod">Cash on delivery</SelectItem>
                  <SelectItem value="razorpay">Razorpay</SelectItem>
                  <SelectItem value="wallet">Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Payment status</Label>
              <Select value={filters.payment_status || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, payment_status: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-pay-status"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Placed from</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-[11px]">Placed to</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
            </div>
            <div>
              <Label className="text-[11px]">Min ₹</Label>
              <Input type="number" placeholder="0" value={filters.amount_min} onChange={(e) => setFilters((f) => ({ ...f, amount_min: e.target.value }))} data-testid="filter-amount-min" />
            </div>
            <div>
              <Label className="text-[11px]">Max ₹</Label>
              <Input type="number" placeholder="∞" value={filters.amount_max} onChange={(e) => setFilters((f) => ({ ...f, amount_max: e.target.value }))} data-testid="filter-amount-max" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="btn-primary-glow" onClick={() => { setPage(1); fetchRows(1, tab, q, filters); }} data-testid="filter-apply">
              Apply filters
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              const clean = { payment_method: "", payment_status: "", date_from: "", date_to: "", amount_min: "", amount_max: "" };
              setFilters(clean); setPage(1); fetchRows(1, tab, q, clean);
            }} data-testid="filter-clear">
              <X className="w-3.5 h-3.5 mr-1" />Clear all
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns} rows={rows} loading={loading}
        onSearch={(v) => { setQ(v); setPage(1); fetchRows(1, tab, v, filters); }}
        searchPlaceholder="Search order # / customer / phone…"
        page={page} limit={limit} total={total} onPage={setPage}
        onRowClick={(r) => nav(`/admin/orders/${r.id}`)}
        selection={sel}
        testId="orders-table"
      />

      <BulkActionBar
        count={sel.count}
        onClear={sel.clear}
        actions={[
          {
            label: (
              <span className="flex items-center gap-1.5">
                <Select value={bulkTarget || "__pick"} onValueChange={(v) => setBulkTarget(v === "__pick" ? "" : v)}>
                  <SelectTrigger className="h-7 border-0 bg-transparent p-0 pr-1 min-w-[120px] focus:ring-0" onClick={(e) => e.stopPropagation()}>
                    <SelectValue placeholder="Mark as…" />
                  </SelectTrigger>
                  <SelectContent onClick={(e) => e.stopPropagation()}>
                    <SelectItem value="__pick">Mark as…</SelectItem>
                    {BULK_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            ),
            onClick: () => runBulkStatus(bulkTarget),
            disabled: !bulkTarget,
            testId: "bulk-status-apply",
          },
        ]}
      />
    </div>
  );
}
